import React, { useState, useEffect, useCallback } from "react";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AllocationsHeader from "./AllocationsHeader";
import StatsStrip from "./StatsStrip";
import SOWSidebar from "./SOWSidebar"; // ← new
import SOWFilterBar from "./SOWFilterBar"; // ← renamed
import AllocationsTable from "./AllocationsTable";
import AllocationWizard from "./AllocationWizard";
import ExceptionsDrawer from "./ExceptionsDrawer";
import { getMyAllocations } from "../../../../../api/macLeader";
import PageInfo from "../../../../../components/common/PageInfo";

const MyAllocations = ({ sows = [] }) => {
  const theme = useTheme();
  const t = theme.custom;

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedSow, setSelectedSow] = useState(sows[0]?.sowId ?? null);
  useEffect(() => {
  if (sows.length > 0 && selectedSow === null) {
    setSelectedSow(sows[0].sowId);
  }
}, [sows]);
  const [statusFilter, setStatusFilter] = useState("ACTIVE"); // ← new
  const [posTypeFilter, setPosTypeFilter] = useState(null); // ← new
  const [associateTypeFilter, setAssociateTypeFilter] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [readOnly, setReadOnly] = useState(false);
  const [exceptionsOpen, setExceptionsOpen] = useState(false);
  const [exceptionsRow, setExceptionsRow]   = useState(null);

  const fetchAllocations = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getMyAllocations();
      setRows(data);
    } catch (err) {
      console.error("Failed to load allocations:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllocations();
  }, [fetchAllocations]);

  const summary = {
    macName: rows[0]?.macName ?? null,
    allocatedHc: rows.filter((r) => r.status === "ACTIVE").length,
    billed: rows.filter(
      (r) => r.status === "ACTIVE" && r.positionType === "BIL",
    ).length,
    unbilled: rows.filter(
      (r) => r.status === "ACTIVE" && r.positionType === "UBL",
    ).length,
    pending: rows.filter((r) =>
      ["SUBMITTED", "PMO_APPROVED", "MOVE_PENDING"].includes(r.status),
    ).length,
    utilisation: (() => {
      const activeRows = rows.filter((r) => r.status === "ACTIVE");
      if (!activeRows.length) return null;
      const usedFte = activeRows.reduce((sum, r) => sum + (r.fte ?? 0), 0);
      const totalFte = activeRows.length * 1.0;
      return Math.round((usedFte / totalFte) * 100);
    })(),
  };

  const filteredRows = rows.filter((row) => {
    const matchesSow =
      !selectedSow || row.sowId === selectedSow || row.sourceSowId === selectedSow;
    const matchesStatus =
      !statusFilter ||
      row.status === statusFilter ||
      (statusFilter === "SUBMITTED" && row.status === "MOVE_PENDING");
    const matchesPosType = !posTypeFilter || row.positionType === posTypeFilter;
    const matchesAssocType =
      !associateTypeFilter ||
      (associateTypeFilter === "CONTRACTOR" ? row.isContractor : !row.isContractor);
    const matchesSearch =
      !search.trim() ||
      row.associateName?.toLowerCase().includes(search.toLowerCase()) ||
      row.positionCode?.toLowerCase().includes(search.toLowerCase());
    return matchesSow && matchesStatus && matchesPosType && matchesAssocType && matchesSearch;
  });

  const normalizeRow = (row) => ({
    ...row,
    associate: {
      id: row.associateId,
      fullName: row.associateName,
      levelCode: row.associateLevelCode || row.levelCode,
      skills: [],   
    },
    position: {
      positionCode: row.positionCode,
      roleTitle: row.roleTitle,
      sowId: row.sowId,
      sowName: row.sowName,
      macName: row.accountName,
    },
    fte: row.fte,
    startDate: row.startDate,
    endDate: row.endDate,
    skills: row.skills,
    deployedSkillNames: row.deployedSkillNames ?? [],
  });

  const handleEdit = (row) => {
    setEditRow(normalizeRow(row));
    setReadOnly(false);
    setWizardOpen(true);
  };
  const handleView = (row) => {
    setEditRow(normalizeRow(row));
    setReadOnly(true);
    setWizardOpen(true);
  };
  const handleClose = () => {
    setWizardOpen(false);
    setEditRow(null);
    setReadOnly(false);
  };
  const handleExceptions = (row) => {
    setExceptionsRow(normalizeRow(row));
    setExceptionsOpen(true);
  };

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}
    >
      <AllocationsHeader
        onNewRequest={() => {
          setEditRow(null);
          setReadOnly(false);
          setWizardOpen(true);
        }}
      />
      <StatsStrip
        macName={summary.macName}
        summary={summary}
        loading={loading}
      />

      <PageInfo message="View and manage all your allocations by SOW"/>

      {/* Body: sidebar + main content */}
      <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
        <SOWSidebar
          sows={sows}
          selectedSow={selectedSow}
          onSelectSow={setSelectedSow}
        />

        {/* Right panel: filter bar + table */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            minWidth: 0,
          }}
        >
          <SOWFilterBar
            search={search}
            onSearchChange={setSearch}
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
            posTypeFilter={posTypeFilter}
            onPosTypeChange={setPosTypeFilter}
            associateTypeFilter={associateTypeFilter}
            onAssociateTypeChange={setAssociateTypeFilter}
          />

          <AllocationsTable
            rows={filteredRows}
            loading={loading}
            onEdit={handleEdit}
            onView={handleView}
            onEnded={fetchAllocations}
            onExceptions={handleExceptions}
          />
        </Box>
      </Box>

      <AllocationWizard
        open={wizardOpen}
        onClose={handleClose}
        onSubmitted={fetchAllocations}
        editRow={editRow}
        readOnly={readOnly}
        sowId={selectedSow}
      />
      <ExceptionsDrawer
        open={exceptionsOpen}
        onClose={() => { setExceptionsOpen(false); setExceptionsRow(null); }}
        row={exceptionsRow}
      />
    </Box>
  );
};

export default MyAllocations;
